import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    IconButton,
    Paper,
    Stack,
    Chip,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    InputAdornment,
    ListSubheader,
    Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { alpha } from '@mui/material/styles';
import { DragDropContext, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot, DroppableProvided, DroppableStateSnapshot, DragStart, OnDragStartResponder } from 'react-beautiful-dnd';
import StrictModeDroppable from './StrictModeDroppable';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash/debounce';
import { useTheme } from '@mui/material/styles';

interface Question {
    id: number;
    text: string;
    options: string[];
    question_bank_id?: number; // ID da questão no banco de questões
    flow_name?: string; // Nome do fluxo ao qual a questão pertence
}

interface MappingCondition {
    questionId: number;
    option: string;
}

interface MappingRule {
    id: string;
    conditions: MappingCondition[];
    resultValue: string;
}

interface GroupedQuestion extends Question {
    occurrences?: number;
}

interface AdvancedMappingEditorProps {
    previousQuestions: Question[];
    currentMappings?: { [key: string]: any };
    onMappingsChange: (mappings: { [key: string]: any }) => void;
}

const AdvancedMappingEditor: React.FC<AdvancedMappingEditorProps> = ({
    previousQuestions,
    currentMappings,
    onMappingsChange
}) => {
    const [mappingRules, setMappingRules] = useState<MappingRule[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [localResultValues, setLocalResultValues] = useState<{ [key: string]: string }>({});
    const theme = useTheme();

    useEffect(() => {
        console.log("AdvancedMappingEditor renderizou com mapeamentos:", currentMappings);
        console.log("Questões anteriores disponíveis:", previousQuestions.length, previousQuestions);

        // Converter mapeamentos existentes para o formato de regras
        if (currentMappings?.type === 'conditional_multiple') {
            try {
                let rules;
                if (Array.isArray(currentMappings.rules)) {
                    // Se já estiver no formato de rules
                    console.log("Usando formato de rules para inicialização:", currentMappings.rules);
                    rules = currentMappings.rules.map((rule, index) => ({
                        id: `rule-${index}-${Date.now()}`, // Adicionar ID único
                        conditions: rule.conditions?.map((condition: { questionId: number; option: string }) => ({
                            questionId: condition.questionId,
                            option: condition.option
                        })) || [],
                        resultValue: rule.value || ''
                    }));
                } else if (currentMappings.values) {
                    // Se estiver no formato antigo com values
                    console.log("Usando formato values para inicialização:", currentMappings.values);
                    rules = Object.entries(currentMappings.values).map(([key, value], index) => ({
                        id: `rule-${index}-${Date.now()}`, // Adicionar ID único
                        conditions: [],
                        resultValue: value as string
                    }));
                }
                
                if (rules && rules.length > 0) {
                    console.log("Regras convertidas:", rules);
                    setMappingRules(rules);
                } else {
                    console.log("Inicializando com regra vazia (nenhuma regra encontrada)");
                    // Criar uma regra inicial vazia
                    setMappingRules([{ 
                        id: `rule-0-${Date.now()}`, // Adicionar ID único
                        conditions: [], 
                        resultValue: '' 
                    }]);
                }
            } catch (error) {
                console.error("Erro ao converter mapeamentos:", error);
                setMappingRules([{ 
                    id: `rule-0-${Date.now()}`, // Adicionar ID único
                    conditions: [], 
                    resultValue: '' 
                }]);
            }
        } else {
            console.log("Inicializando com regra vazia (tipo incompatível ou primeira inicialização)");
            // Criar uma regra inicial vazia
            setMappingRules([{ 
                id: `rule-0-${Date.now()}`, // Adicionar ID único
                conditions: [], 
                resultValue: '' 
            }]);
        }

        // Habilitar o drag-and-drop apenas após a montagem do componente
        const animation = requestAnimationFrame(() => setEnabled(true));
        return () => {
            cancelAnimationFrame(animation);
            setEnabled(false);
        };
    }, [currentMappings, previousQuestions]);

    // Função para adicionar uma nova regra
    const handleAddRule = () => {
        const newRule = {
            id: `rule-${mappingRules.length}-${Date.now()}`, // Adicionar ID único
            conditions: [],
            resultValue: ''
        };

        const updatedRules = [...mappingRules, newRule];
        setMappingRules(updatedRules);
        updateMappings(updatedRules);
    };

    // Função para adicionar uma condição a uma regra existente
    const handleAddCondition = (ruleIndex: number, questionId?: number) => {
        const newRules = [...mappingRules];
        
        // Se um questionId foi fornecido, use-o; caso contrário, use o primeiro disponível
        const selectedQuestionId = questionId || (previousQuestions.length > 0 ? previousQuestions[0].id : -1);
        const selectedQuestion = previousQuestions.find(q => q.id === selectedQuestionId);
        
        const newCondition: MappingCondition = {
            questionId: selectedQuestionId,
            option: selectedQuestion?.options[0] || ''
        };
        
        console.log('Adicionando nova condição:', newCondition);
        
        if (!newRules[ruleIndex].conditions) {
            newRules[ruleIndex].conditions = [];
        }
        
        newRules[ruleIndex].conditions.push(newCondition);
        setMappingRules(newRules);
        updateMappings(newRules);
    };

    const handleUpdateCondition = (
        ruleIndex: number,
        conditionIndex: number,
        field: keyof MappingCondition,
        value: string | number
    ) => {
        console.log(`Atualizando condição: ruleIndex=${ruleIndex}, conditionIndex=${conditionIndex}, field=${field}, value=`, value);
        
        const newRules = [...mappingRules];
        const condition = newRules[ruleIndex].conditions[conditionIndex];

        if (field === 'questionId') {
            const question = previousQuestions.find(q => q.id === value);
            if (question) {
                condition.questionId = value as number;
                // Garantir que a opção seja definida apenas se a questão tiver opções
                if (question.options && question.options.length > 0) {
                    condition.option = question.options[0];
                }
            }
        } else {
            condition[field] = value as never;
        }

        setMappingRules(newRules);
        updateMappings(newRules);
        
        console.log('Regras atualizadas:', newRules);
    };

    // Criar versão com debounce da função updateMappings
    const debouncedUpdateMappings = useCallback(
        debounce((rules: MappingRule[]) => {
            const newMappings = {
                type: 'conditional_multiple',
                values: {},
                rules: rules.map(rule => ({
                    conditions: rule.conditions.map(condition => {
                        const question = previousQuestions.find(q => q.id === condition.questionId);
                        const questionBankId = question?.question_bank_id || condition.questionId;
                        return {
                            questionId: questionBankId,
                            option: condition.option,
                        };
                    }),
                    value: rule.resultValue
                }))
            };
            onMappingsChange(newMappings);
        }, 300),
        [previousQuestions, onMappingsChange]
    );

    // Atualizar o handleUpdateResultValue para usar estado local
    const handleUpdateResultValue = (ruleIndex: number, value: string) => {
        const ruleId = mappingRules[ruleIndex].id;
        
        // Atualizar o estado local imediatamente
        setLocalResultValues(prev => ({
            ...prev,
            [ruleId]: value
        }));

        // Atualizar o estado global com debounce
        const newMappingRules = [...mappingRules];
        newMappingRules[ruleIndex].resultValue = value;
        setMappingRules(newMappingRules);
        debouncedUpdateMappings(newMappingRules);
    };

    const handleRemoveRule = (ruleIndex: number) => {
        const newMappingRules = mappingRules.filter((_, index) => index !== ruleIndex);
        setMappingRules(newMappingRules);
        updateMappings(newMappingRules);
    };

    const handleRemoveCondition = (ruleIndex: number, conditionIndex: number) => {
        const newMappingRules = [...mappingRules];
        
        // Garantir que haja pelo menos uma condição
        if (newMappingRules[ruleIndex].conditions.length <= 1) {
            handleRemoveRule(ruleIndex);
            return;
        }
        
        newMappingRules[ruleIndex].conditions = newMappingRules[ruleIndex].conditions
            .filter((_, index) => index !== conditionIndex);
        
        setMappingRules(newMappingRules);
        updateMappings(newMappingRules);
    };

    const updateMappings = (rules: MappingRule[]) => {
        // Converter regras para o formato de mapeamento esperado
        const newMappings = {
            type: 'conditional_multiple',
            values: {},  // Manter compatibilidade com formato antigo
            rules: rules.map(rule => {
                // Adicionar logs para depuração
                console.log("Processando regra para atualização:", rule);
                
                if (!rule.conditions || rule.conditions.length === 0) {
                    console.log("ATENÇÃO: Regra sem condições:", rule);
                }
                
                return {
                    conditions: rule.conditions.map(condition => {
                        // Buscar a questão pelo ID para obter seu question_bank_id
                        const question = previousQuestions.find(q => q.id === condition.questionId);
                        const questionBankId = question?.question_bank_id || condition.questionId;
                        
                        console.log(`Mapeando questionId: ${condition.questionId} para questionBankId: ${questionBankId}, opção: ${condition.option}`);
                        
                        return {
                            questionId: questionBankId, // Usar o ID do banco de questões ao invés do ID do fluxo
                            option: condition.option,
                        };
                    }),
                    value: rule.resultValue
                };
            })
        };

        console.log("Enviando mapeamentos atualizados:", newMappings);
        onMappingsChange(newMappings);
    };

    const onDragStart: OnDragStartResponder = (start) => {
        setIsDragging(true);
        setDraggedIndex(start.source.index);
    };

    const onDragEnd = (result: DropResult) => {
        setIsDragging(false);
        setDraggedIndex(null);
        if (!result.destination) return;

        const newRules = Array.from(mappingRules);
        const [reorderedItem] = newRules.splice(result.source.index, 1);
        newRules.splice(result.destination.index, 0, reorderedItem);

        setMappingRules(newRules);
        updateMappings(newRules);
    };

    // Função para organizar as questões por fluxo
    const getOrganizedQuestions = () => {
        // Agrupar questões por fluxo
        const questionsByFlow: Record<string, Question[]> = {};
        
        // Primeiro passo: agrupar por fluxo
        previousQuestions.forEach(question => {
            const flowName = question.flow_name || 'Sem fluxo';
            
            if (!questionsByFlow[flowName]) {
                questionsByFlow[flowName] = [];
            }
            
            // Verificar se já existe uma questão com o mesmo ID no mesmo fluxo
            const existingQuestion = questionsByFlow[flowName].find(
                q => q.id === question.id && q.text === question.text
            );
            
            if (!existingQuestion) {
                questionsByFlow[flowName].push(question);
            }
        });
        
        // Segundo passo: ordenar cada grupo por ID
        Object.keys(questionsByFlow).forEach(flowName => {
            questionsByFlow[flowName].sort((a, b) => {
                const idA = a.question_bank_id || a.id;
                const idB = b.question_bank_id || b.id;
                return idA - idB;
            });
        });
        
        // Terceiro passo: criar lista final com cabeçalhos de grupo
        const organizedQuestions: (Question | { isGroupHeader: true, flowName: string })[] = [];
        
        // Ordenar os fluxos alfabeticamente
        const sortedFlows = Object.keys(questionsByFlow).sort();
        
        sortedFlows.forEach(flowName => {
            // Adicionar cabeçalho do grupo
            organizedQuestions.push({
                isGroupHeader: true,
                flowName: flowName
            } as any);
            
            // Adicionar questões do grupo
            questionsByFlow[flowName].forEach(question => {
                organizedQuestions.push(question);
            });
        });
        
        return organizedQuestions;
    };

    // Verificar se há questões anteriores disponíveis
    if (previousQuestions.length === 0) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                Nenhuma questão anterior disponível para mapeamento. Adicione questões ao fluxo primeiro.
            </Alert>
        );
    }

    if (!enabled) {
        return (
            <Box sx={{ p: 2, bgcolor: theme => alpha(theme.palette.mode === 'dark' ? '#000' : '#f5f5f5', 0.3), borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Mapeamento Avançado de Variáveis
                </Typography>
                <Box sx={{ opacity: 0.5 }}>Carregando...</Box>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            p: { xs: 2, sm: 3 }, 
            bgcolor: theme => alpha(theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5', 0.3), 
            borderRadius: 2,
            maxWidth: '100%',
            overflow: 'hidden'
        }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Mapeamento Avançado de Variáveis
            </Typography>
            
            <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <StrictModeDroppable droppableId="rules-list">
                    {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
                        <Box 
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{ 
                                mb: 2,
                                transition: 'all 0.2s ease',
                                opacity: isDragging ? 0.7 : 1
                            }}
                        >
                            {mappingRules.map((rule, index) => (
                                <React.Fragment key={rule.id}>
                                    <Draggable 
                                        draggableId={rule.id}
                                        index={index}
                                    >
                                        {(dragProvided: DraggableProvided, dragSnapshot: DraggableStateSnapshot) => (
                                            <Paper
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                elevation={dragSnapshot.isDragging ? 6 : 1}
                                                sx={{
                                                    p: 2,
                                                    mb: 2,
                                                    bgcolor: theme => 
                                                        dragSnapshot.isDragging 
                                                        ? theme.palette.background.paper
                                                        : alpha(
                                                            theme.palette.mode === 'dark' 
                                                            ? theme.palette.background.paper 
                                                            : '#fff', 
                                                            theme.palette.mode === 'dark' ? 0.2 : 0.7
                                                        ),
                                                    borderRadius: 1,
                                                    transition: 'all 0.2s ease',
                                                    transform: dragSnapshot.isDragging 
                                                        ? 'scale(1.02)' 
                                                        : 'scale(1)',
                                                    '&:hover': {
                                                        bgcolor: 'background.paper',
                                                        transform: 'scale(1.01)',
                                                    },
                                                    position: 'relative',
                                                    zIndex: dragSnapshot.isDragging ? 1 : 'auto',
                                                    border: theme => 
                                                        theme.palette.mode === 'dark' 
                                                        ? `1px solid ${alpha(theme.palette.divider, 0.1)}` 
                                                        : 'none'
                                                }}
                                            >
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    mb: 2,
                                                    cursor: 'move'
                                                }}>
                                                    <IconButton 
                                                        size="small" 
                                                        {...dragProvided.dragHandleProps}
                                                        sx={{ 
                                                            mr: 1,
                                                            color: dragSnapshot.isDragging 
                                                                ? 'primary.main'
                                                                : 'text.secondary',
                                                            '&:hover': {
                                                                color: 'primary.main'
                                                            }
                                                        }}
                                                    >
                                                        <DragIndicatorIcon />
                                                    </IconButton>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        sx={{ 
                                                            flexGrow: 1,
                                                            color: dragSnapshot.isDragging 
                                                                ? 'primary.main'
                                                                : 'text.primary'
                                                        }}
                                                    >
                                                        Regra {index + 1}
                                                    </Typography>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleRemoveRule(index)}
                                                        sx={{ 
                                                            ml: 1,
                                                            color: 'error.main',
                                                            '&:hover': {
                                                                bgcolor: theme => alpha(theme.palette.error.main, 0.1)
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>

                                                {/* Condições */}
                                                <Box sx={{ 
                                                    mb: 2,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2
                                                }}>
                                                    <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                                                        Condições:
                                                    </Typography>
                                                    {rule.conditions.map((condition, conditionIndex) => (
                                                        <React.Fragment key={conditionIndex}>
                                                            <Box 
                                                                sx={{ 
                                                                    display: 'flex',
                                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                                    gap: 2,
                                                                    width: '100%'
                                                                }}
                                                            >
                                                                <FormControl 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        flex: 2,
                                                                        minWidth: { xs: '100%', sm: '300px' }
                                                                    }}
                                                                >
                                                                    <Autocomplete
                                                                        size="small"
                                                                        options={getOrganizedQuestions()}
                                                                        getOptionLabel={(option) => {
                                                                            if ((option as any).isGroupHeader) return (option as any).flowName;
                                                                            
                                                                            const questionOption = option as Question;
                                                                            const questionId = questionOption.question_bank_id || questionOption.id;
                                                                            const occurrences = previousQuestions.filter(
                                                                                q => (q.question_bank_id || q.id) === questionId && 
                                                                                     q.flow_name === questionOption.flow_name
                                                                            ).length;
                                                                            
                                                                            return questionOption.text;
                                                                        }}
                                                                        renderOption={(props, option) => {
                                                                            // Se for um cabeçalho de grupo, renderizar como tal
                                                                            if ((option as any).isGroupHeader) {
                                                                                return (
                                                                                    <ListSubheader 
                                                                                        key={(option as any).flowName}
                                                                                        sx={{ 
                                                                                            fontWeight: 'bold', 
                                                                                            bgcolor: 'background.paper',
                                                                                            color: 'primary.main',
                                                                                            py: 1,
                                                                                            px: 2,
                                                                                            position: 'sticky',
                                                                                            top: 0,
                                                                                            zIndex: 1
                                                                                        }}
                                                                                    >
                                                                                        {(option as any).flowName}
                                                                                    </ListSubheader>
                                                                                );
                                                                            }
                                                                            
                                                                            // Se for uma questão normal, renderizar normalmente
                                                                            const questionOption = option as Question;
                                                                            const questionId = questionOption.question_bank_id || questionOption.id;
                                                                            const occurrences = previousQuestions.filter(
                                                                                q => (q.question_bank_id || q.id) === questionId && 
                                                                                     q.flow_name === questionOption.flow_name
                                                                            ).length;
                                                                            
                                                                            return (
                                                                                <li {...props}>
                                                                                    <Box>
                                                                                        <Box>{questionOption.text}</Box>
                                                                                        <Box sx={{ 
                                                                                            color: 'text.secondary',
                                                                                            fontSize: '0.85em'
                                                                                        }}>
                                                                                            ID {questionId}, Fluxo: {questionOption.flow_name}
                                                                                            {occurrences > 1 ? ` (${occurrences}x)` : ''}
                                                                                        </Box>
                                                                                    </Box>
                                                                                </li>
                                                                            );
                                                                        }}
                                                                        renderInput={(params) => (
                                                                            <TextField 
                                                                                {...params} 
                                                                                label="Questão" 
                                                                                InputProps={{
                                                                                    ...params.InputProps,
                                                                                    startAdornment: (
                                                                                        <>
                                                                                            <InputAdornment position="start">
                                                                                                <SearchIcon fontSize="small" />
                                                                                            </InputAdornment>
                                                                                            {params.InputProps.startAdornment}
                                                                                        </>
                                                                                    )
                                                                                }}
                                                                            />
                                                                        )}
                                                                        value={previousQuestions.find(q => q.id === condition.questionId) || null}
                                                                        onChange={(_, newValue) => {
                                                                            if (newValue && !(newValue as any).isGroupHeader) {
                                                                                handleUpdateCondition(index, conditionIndex, 'questionId', (newValue as Question).id);
                                                                            }
                                                                        }}
                                                                        isOptionEqualToValue={(option, value) => {
                                                                            if ((option as any).isGroupHeader) return false;
                                                                            return (option as Question).id === (value as Question).id;
                                                                        }}
                                                                    />
                                                                </FormControl>
                                                                
                                                                <FormControl 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        flex: 1,
                                                                        minWidth: { xs: '100%', sm: '200px' }
                                                                    }}
                                                                >
                                                                    <InputLabel>Opção</InputLabel>
                                                                    <Select
                                                                        value={condition.option || ''}
                                                                        onChange={(e) => handleUpdateCondition(index, conditionIndex, 'option', e.target.value)}
                                                                        label="Opção"
                                                                    >
                                                                        {previousQuestions
                                                                            .find(q => q.id === condition.questionId)
                                                                            ?.options.map((option) => (
                                                                                <MenuItem key={option} value={option}>
                                                                                    {option}
                                                                                </MenuItem>
                                                                            ))}
                                                                    </Select>
                                                                </FormControl>

                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleRemoveCondition(index, conditionIndex)}
                                                                    sx={{ 
                                                                        alignSelf: { xs: 'flex-end', sm: 'center' },
                                                                        color: 'error.main',
                                                                        '&:hover': {
                                                                            bgcolor: theme => alpha(theme.palette.error.main, 0.1)
                                                                        }
                                                                    }}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Box>
                                                            
                                                            {/* Adiciona o "E" entre as condições */}
                                                            {conditionIndex < rule.conditions.length - 1 && (
                                                                <Box 
                                                                    sx={{ 
                                                                        display: 'flex', 
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        py: 1
                                                                    }}
                                                                >
                                                                    <Typography 
                                                                        variant="body2" 
                                                                        sx={{ 
                                                                            px: 2,
                                                                            py: 0.5,
                                                                            bgcolor: 'primary.main',
                                                                            color: 'primary.contrastText',
                                                                            borderRadius: 1,
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 500
                                                                        }}
                                                                    >
                                                                        E
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                    
                                                    <Button
                                                        startIcon={<AddIcon />}
                                                        onClick={() => handleAddCondition(index)}
                                                        size="small"
                                                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                                                    >
                                                        Adicionar Condição
                                                    </Button>
                                                </Box>

                                                {/* Valor Resultante */}
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                                                        Valor Resultante:
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={localResultValues[rule.id] ?? rule.resultValue ?? ''}
                                                        onChange={(e) => handleUpdateResultValue(index, e.target.value)}
                                                        placeholder="Digite o valor resultante..."
                                                    />
                                                </Box>
                                            </Paper>
                                        )}
                                    </Draggable>
                                    
                                    {index < mappingRules.length - 1 && (
                                        <Box 
                                            sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                my: 2
                                            }}
                                        >
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    px: 3,
                                                    py: 0.5,
                                                    bgcolor: 'secondary.main',
                                                    color: 'secondary.contrastText',
                                                    borderRadius: 1,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                    boxShadow: theme => 
                                                        theme.palette.mode === 'dark' 
                                                        ? '0px 0px 10px rgba(0,0,0,0.3)' 
                                                        : 'none'
                                                }}
                                            >
                                                OU
                                            </Typography>
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            <Button
                startIcon={<AddIcon />}
                onClick={handleAddRule}
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
            >
                Adicionar Nova Regra
            </Button>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                <strong>Como funciona:</strong> Quando todas as condições de uma regra forem atendidas, 
                a variável será substituída pelo valor resultante. Se várias regras forem atendidas, a primeira será usada.
            </Typography>
        </Box>
    );
};

export default AdvancedMappingEditor; 